import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import toPlainObject from 'lodash/toPlainObject';
import { replace } from 'connected-react-router';

import { getCompareRentQuery } from '../../utils/queries';
import api from '../../utils/api';
// Actions
import { getComparables } from '../../flux/actions/AddressActions';
import { getMarketTrends, getTopMarkets } from '../../flux/actions/AreaActions';

// Models
import Filter from '../../models/Filter';

// Misc / Utils
import filterUtils from '../../utils/filterUtils';
import { queryObjectToQueryString } from '../../utils/urlUtils';

import { augmentError } from '../../utils/errors';
import constants from '../../flux/constants';

// Types
import type { QueryDataObjectType } from '../../types/QueryDataObjectType';
import type { FilterApiType } from '../../types/ApiResponseTypes';
import type { PartialFilterType } from '../../types/Filters';
type objectType = {
  [key: string]: string | Array<string>;
};

/**
 * All filter changes go through `handlePMRFilterChange`. Updating or resetting a filter
 * will initially result in a url change. The PriceMyRentalResults page will listen for
 * url filter param changes and will call `loadFilterFromUrl`. `loadFilterFromUrl` will
 * parse the url filter params and ultimately update the filter on the store.
 *
 * This flow guarantees that the url will be sharable and that our filter UI reflects
 * the filter in the url and store.
 */
export const handlePMRFilterChange = (partialFilter?: PartialFilterType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    try {
      const { filter: currentFilter, address } = getState();
      let partialFilterForUrl = {};

      if (partialFilter) {
        partialFilterForUrl = _getPartialFilterForUrl(currentFilter, partialFilter);
      }

      const { filterForGraphQL, filterForUrl } = filterUtils.partialFilterToFilterParams(partialFilterForUrl);

      const addressDetailsParams = {
        name: `${address.street}, ${address.city}, ${address.state} ${address.zip}`,
        street: address.street,
        city: address.city,
        state: address.state,
        zip: address.zip,
        unit: address.unit,
        // the address price will be overwritten by the price filter (if one is set)
        price: address.rentZestimate,
      };
      const params: QueryDataObjectType = assign({}, addressDetailsParams, filterForGraphQL);

      await dispatch(getComparables(params));

      return dispatch(_updateUrlWithFilterParams(toPlainObject(filterForUrl)));
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#handlePMRFilterChange',
        partialFilter,
      });

      throw error;
    }
  };
};

export const handleMarketTrendsFilterChange = (partialFilter: PartialFilterType) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    try {
      const {
        filter,
        marketTrends: {
          resourceId,
          rentCompare: { otherResourceId },
        },
      } = getState();
      const { filterForUrl, filterForGraphQL } = filterUtils.partialFilterToFilterParams(
        _getPartialFilterForUrl(filter, partialFilter),
      );
      if (resourceId) {
        await dispatch(
          getMarketTrends({
            ...filterForGraphQL,
            resourceId,
            otherResourceId,
          }),
        );
      } else {
        await dispatch(getTopMarkets(filterForGraphQL));
      }

      dispatch(_updateFilter(partialFilter));
      return dispatch(_updateUrlWithFilterParams(toPlainObject(filterForUrl)));
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#handleRentalMarketFilterChange',
        partialFilter,
      });

      throw error;
    }
  };
};

export const handleCompareMarketChange = (
  partialFilter: PartialFilterType,
  otherResourceId: string,
  resourceId: string,
) => {
  // @ts-ignore
  return async (dispatch, getState) => {
    try {
      const { filter } = getState();
      const { filterForGraphQL } = filterUtils.partialFilterToFilterParams(
        _getPartialFilterForUrl(filter, partialFilter),
      );

      const { data, errors } = await dispatch(
        api.area.graphQlMarketTrends(getCompareRentQuery({ resourceId, otherResourceId, ...filterForGraphQL })),
      );

      return { data, errors };
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#handleCompareMarketChange',
        partialFilter,
      });

      throw error;
    }
  };
};

const _getPartialFilterForUrl = (currentFilter: FilterApiType, partialFilter?: PartialFilterType) => {
  const newFilter = toPlainObject(new Filter(assign({}, currentFilter, partialFilter)));

  // get relevant filters, ie: non-default filters
  const filterDiff = filterUtils.getFilterDiff({
    baseFilter: toPlainObject(new Filter()),
    editedFilter: newFilter,
  });

  /**
   * When default values are truthy (ex: propertyTypes) filterDiff will only include
   * falsey values, ex: { propertyType : { house: false, townhouse: false } }. Since
   * we actually want url params to contain truthy propertyTypes
   * (propertyType=apartments-condos), we need to use the entire filter.
   *
   * Exception: to support activatedDays & deactivatedDays filters, we need to pass
   * both default (truthy) values for activeTypes
   */
  const partialFilterForUrl = pick(newFilter, Object.keys({ ...filterDiff, ...{ activeTypes: null } }));
  return partialFilterForUrl;
};

const _updateFilter = (partialFilter: PartialFilterType) => {
  // @ts-ignore
  return (dispatch) => {
    try {
      return dispatch({
        type: constants.UPDATE_FILTER,
        payload: new Filter(assign(toPlainObject(new Filter()), partialFilter)),
      });
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#_updateFilter',
        partialFilter: partialFilter,
      });

      throw error;
    }
  };
};

export const resetFilter = () => {
  // @ts-ignore
  return (dispatch) => {
    try {
      return dispatch({
        type: constants.UPDATE_FILTER,
        payload: new Filter(),
      });
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#resetFilter',
      });

      throw error;
    }
  };
};

const _updateUrlWithFilterParams = (filterParams: objectType) => {
  // @ts-ignore
  return (dispatch, getState) => {
    try {
      const { pathname: currPathname, query } = getState().router.location;

      const queryParams = { ...filterParams };
      if (query.unit) {
        queryParams.unit = query.unit;
      }

      const newUrl = currPathname + queryObjectToQueryString(toPlainObject(queryParams));

      return dispatch(replace(newUrl));
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#_updateUrlWithFilterParams',
      });

      throw error;
    }
  };
};

export const loadFilterFromUrl = (query: objectType) => {
  // @ts-ignore
  return async (dispatch) => {
    try {
      const filterFromUrl = filterUtils.queryObjToFilterObj(toPlainObject(query));
      if (isEmpty(filterFromUrl)) {
        return dispatch(resetFilter());
      } else {
        return dispatch(_updateFilter(filterFromUrl));
      }
    } catch (error) {
      augmentError(error, {
        action: 'Error in FilterActions#loadFilterFromUrl',
      });

      throw error;
    }
  };
};
